import React, { useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  formatPrice,
  getShippingRate,
  siteConfig,
} from "../Datas/productData";
import "../css/checkout.css";

const initialForm = {
  contact: "",
  country: "US",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
};
const countries = [
  ["US", "United States"],
  ["CA", "Canada"],
  ["GB", "United Kingdom"],
  ["AU", "Australia"],
  ["NZ", "New Zealand"],
  ["HK", "Hong Kong SAR"],
  ["CN", "China"],
  ["SG", "Singapore"],
  ["MY", "Malaysia"],
  ["JP", "Japan"],
  ["KR", "South Korea"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["IT", "Italy"],
  ["NL", "Netherlands"],
];
const required = [
  "contact",
  "country",
  "firstName",
  "lastName",
  "address",
  "city",
  "postalCode",
];

const CheckOut = () => {
  const formRef = useRef(null);
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find(
            (candidate) => String(candidate.id) === String(item.id)
          );
          if (!product) return null;
          const quantity = Math.max(1, Number(item.quantity) || 1);
          return {
            ...item,
            product,
            quantity,
            lineTotal: product.price * quantity,
          };
        })
        .filter(Boolean),
    [cartItems]
  );
  const subtotal = cartLines.reduce((sum, item) => sum + item.lineTotal, 0);
  const shipping = getShippingRate();
  const total = subtotal + shipping;
  const deliveryEstimate =
    form.country === "US" ? "5–12 business days" : "7–21 business days";
  const handleChange = ({ target }) => {
    setForm((current) => ({ ...current, [target.name]: target.value }));
    setErrors((current) => ({ ...current, [target.name]: "" }));
  };
  const handlePlaceOrder = (event) => {
    event.preventDefault();
    const nextErrors = {};
    required.forEach((name) => {
      if (!form[name].trim()) nextErrors[name] = "This field is required.";
    });
    if (form.contact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact))
      nextErrors.contact = "Enter a valid email address.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      formRef.current?.elements.namedItem(Object.keys(nextErrors)[0])?.focus();
      return;
    }
    setIsSubmitting(true);
    const orderNo = `${Date.now()}${Math.floor(
      100000 + Math.random() * 900000
    )}`;
    const order = {
      orderNumber: orderNo,
      placedAt: new Date().toISOString(),
      customer: {
        contact: form.contact.trim(),
        name: `${form.firstName.trim()} ${form.lastName.trim()}`,
      },
      delivery: {
        country: form.country,
        company: form.company.trim(),
        address: form.address.trim(),
        apartment: form.apartment.trim(),
        city: form.city.trim(),
        region: form.region.trim(),
        postalCode: form.postalCode.trim(),
      },
      shippingMethod: "Tracked shipping",
      payment: "Secure online payment",
      items: cartLines.map((item) => ({
        id: item.product.id,
        name: item.product.name,
        image: item.product.images[0],
        quantity: item.quantity,
        size: item.size || "",
        color: item.color || "",
        unitPrice: item.product.price,
        lineTotal: item.lineTotal,
      })),
      itemCount: cartLines.reduce((count, item) => count + item.quantity, 0),
      subtotal,
      shipping,
      total,
      currency: siteConfig.currency.code,
    };
    try {
      window.sessionStorage.setItem(
        siteConfig.storage.orderKey,
        JSON.stringify(order)
      );
    } catch {}
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo,
      amount: total.toFixed(2),
      currency: siteConfig.currency.code,
      email: form.contact.trim(),
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: cartLines[0].product.name,
    });
    window.location.href(`${origin}/create_payment.php?${params.toString()}`);
  };
  if (!cartLines.length)
    return (
      <main className="checkout-empty">
        <p className="checkout-eyebrow">Checkout</p>
        <h1>Your bag needs something first.</h1>
        <p>Add a piece to your bag before starting checkout.</p>
        <Link className="checkout-primary-button" to="/shop">
          Browse the collection
        </Link>
      </main>
    );
  const field = (name, label, options = {}) => (
    <div className="checkout-field">
      <label htmlFor={name}>
        {label}
        {options.optional && <span> Optional</span>}
      </label>
      <input
        id={name}
        name={name}
        type={options.type || "text"}
        value={form[name]}
        onChange={handleChange}
        autoComplete={options.autoComplete}
        aria-invalid={Boolean(errors[name])}
      />
      {errors[name] && (
        <p className="checkout-field__error" role="alert">
          {errors[name]}
        </p>
      )}
    </div>
  );
  return (
    <main className="checkout-page" aria-labelledby="checkout-title">
      <div className="checkout-page__intro">
        <nav>
          <Link to="/cartpage">Bag</Link>
          <span> / </span>
          <span>Information</span>
          <span> / </span>
          <span>Payment</span>
        </nav>
        <p className="checkout-eyebrow">Secure checkout</p>
        <h1 id="checkout-title">Complete your order</h1>
        <p>
          Enter your delivery details, then continue to our secure payment page.
        </p>
      </div>
      <div className="checkout-page__layout">
        <form
          className="checkout-form"
          ref={formRef}
          onSubmit={handlePlaceOrder}
          noValidate
        >
          <section className="checkout-section">
            <div className="checkout-section__heading">
              <span>01</span>
              <div>
                <h2>Contact</h2>
                <p>Your receipt and order updates will be sent here.</p>
              </div>
            </div>
            {field("contact", "Email address", {
              type: "email",
              autoComplete: "email",
            })}
          </section>
          <section className="checkout-section">
            <div className="checkout-section__heading">
              <span>02</span>
              <div>
                <h2>Delivery address</h2>
                <p>Available destinations exclude restricted markets.</p>
              </div>
            </div>
            <div className="checkout-field">
              <label htmlFor="country">Country / region</label>
              <select
                id="country"
                name="country"
                value={form.country}
                onChange={handleChange}
              >
                {countries.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="checkout-field-row">
              {field("firstName", "First name", { autoComplete: "given-name" })}
              {field("lastName", "Last name", { autoComplete: "family-name" })}
            </div>
            {field("company", "Company", {
              optional: true,
              autoComplete: "organization",
            })}
            {field("address", "Street address", {
              autoComplete: "address-line1",
            })}
            {field("apartment", "Apartment, suite, etc.", {
              optional: true,
              autoComplete: "address-line2",
            })}
            <div className="checkout-field-row checkout-field-row--three">
              {field("city", "City", { autoComplete: "address-level2" })}
              {field("region", "State / province", {
                optional: true,
                autoComplete: "address-level1",
              })}
              {field("postalCode", "Postal code", {
                autoComplete: "postal-code",
              })}
            </div>
          </section>
          <section className="checkout-section">
            <div className="checkout-section__heading">
              <span>03</span>
              <div>
                <h2>Shipping method</h2>
                <p>Tracked delivery for every order.</p>
              </div>
            </div>
            <div className="checkout-choice">
              <span>
                <strong>Tracked shipping</strong>
                <small>Estimated {deliveryEstimate}</small>
              </span>
              <b>{formatPrice(shipping)}</b>
            </div>
          </section>
          <div className="checkout-form__actions">
            <Link to="/cartpage">← Return to bag</Link>
            <button
              className="checkout-primary-button"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Redirecting…"
                : `Pay now · ${formatPrice(total)}`}
            </button>
          </div>
          <p className="checkout-form__legal">
            Payment details are entered only on the secure payment page. We do
            not collect card numbers on this form. By selecting Pay now, you
            agree to our <Link to="/terms">Terms of Service</Link> and
            acknowledge our <Link to="/privacy">Privacy Policy</Link> and{" "}
            <Link to="/returns">Returns Policy</Link>.
          </p>
        </form>
        <aside className="checkout-summary">
          <div className="checkout-summary__title">
            <div>
              <p className="checkout-eyebrow">Your bag</p>
              <h2>Order summary</h2>
            </div>
            <Link to="/cartpage">Edit</Link>
          </div>
          <div className="checkout-summary__items">
            {cartLines.map((item) => (
              <article
                className="checkout-summary-item"
                key={`${item.id}-${item.size}-${item.color}`}
              >
                <div className="checkout-summary-item__image">
                  <img
                    src={item.product.images[0]}
                    alt=""
                    width="1120"
                    height="1400"
                  />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  <p>{[item.color, item.size].filter(Boolean).join(" / ")}</p>
                </div>
                <strong>{formatPrice(item.lineTotal)}</strong>
              </article>
            ))}
          </div>
          <dl className="checkout-summary__totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatPrice(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{formatPrice(shipping)}</dd>
            </div>
            <div className="checkout-summary__total">
              <dt>
                Total <small>{siteConfig.currency.code}</small>
              </dt>
              <dd>{formatPrice(total)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
};
export default CheckOut;
